import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Observable, of, throwError } from 'rxjs';
import {
  takeUntil, filter, concatMap, map, tap,
  catchError
} from 'rxjs/operators';

import { QuoteCustomPropertyKeyType } from 'graphql/models/extended';
import { DocumentVersionInput, Customer } from 'graphql/models/generated';
import { OfferBase } from 'models/offer.model';
import { BaseComponent } from 'shared/base/base.component';
import { CoreDataService } from 'shared/services/core-data.service';
import { OfferService } from 'shared/services/offer.service';
import { StoreService } from 'shared/services/store.service';

import { CustomerBasicInfoComponent } from '../basic-info/basic-info.component';

@Component({
  selector: 'app-customer-basic-info-modal',
  templateUrl: './basic-info-modal.component.html',
  styleUrls: ['./basic-info-modal.component.scss']
})
export class CustomerBasicInfoModalComponent extends BaseComponent implements OnInit {
  @ViewChild(CustomerBasicInfoComponent) basicInfoComponent!: CustomerBasicInfoComponent;

  customerId?: string;

  docVersion!: DocumentVersionInput;

  get form(): UntypedFormGroup {
    return this.basicInfoComponent.form;
  }

  constructor(
    protected coreDataService: CoreDataService,
    protected storeService: StoreService,
    private offerService: OfferService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    this.storeService.getCurrentOffer()
      .pipe(
        filter((o: OfferBase) => Boolean(o && o.documentId)),
        takeUntil(this.destroyed$)
      ).subscribe((offer: OfferBase) => {
        this.docVersion = { documentId: offer.documentId, revision: offer.revision };
      });
    // Don't use combineLatest, because current offer and customer obs don't rely on each other.
    this.storeService.getCurrentCustomer()
      .pipe(
        filter((c: Customer) => Boolean(c && c.id)),
        takeUntil(this.destroyed$)
      ).subscribe((customer: Customer) => {
        this.customerId = customer.id;
      });
  }

  onSubmit(): Observable<Customer> {
    if (this.customerId) {
      return this.coreDataService.updateCustomer(this.customerId, this.form.value);
    }
    
    return this.coreDataService.createCustomer(this.form.value, this.docVersion)
      .pipe(
        concatMap((customer: Customer): Observable<Customer> => {
          if (this.docVersion && this.docVersion.documentId) {
            return this.offerService.updatePricingBasedOnConditions(this.docVersion, customer)
              .pipe(map(() => customer));
          }

          return of(customer);
        }),
        // This is a fix to remove an assigned contactPerson from Quote, if a new customer is created
        concatMap((customer: Customer): Observable<Customer> => {
          if (this.docVersion) {
            return this.offerService.updateOffer(this.docVersion, [{
              key: QuoteCustomPropertyKeyType.ContactPersonID,
              value: '',
              propertyType: 'String'
            }]).pipe(
              map(() => customer)
            );
          }

          return of(customer);
        }),
        tap((customer) => {
          this.storeService.setOfferUpdate({ customer, contactPerson: undefined });
        }),
        catchError((error) => {
          const message = error?.message || '';
          const control = this.basicInfoComponent.form.get('customerNumber');
          if (control && message.toLowerCase().includes('customernumber')) {
            control.setErrors({ customerNumberConflict: 'Customer creation failed.' });
          }
          return throwError(() => error);
        })
      );
  }
}
